import { useCallback } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useDispatch } from 'react-redux';
import { toggleFilterDisplay } from '../../../actions/filterActions.ts';
import { trackEvent, eventNameConstants } from 'dibs-tracking';
import { useSbSelector } from '../../../reducers/useSbSelector.ts';

import { type useRefineMenuClose_appliedFilters$key } from './__generated__/useRefineMenuClose_appliedFilters.graphql.ts';

const appliedFiltersFragment = graphql`
    fragment useRefineMenuClose_appliedFilters on SearchBrowseParsedParam @relay(plural: true) {
        name
        values {
            urlLabel
        }
    }
`;

export type HandleRefineMenuClose = ({ areFiltersApplied }: { areFiltersApplied: boolean }) => void;

export const useRefineMenuClose = (
    appliedFiltersRef: useRefineMenuClose_appliedFilters$key
): HandleRefineMenuClose => {
    const hasNewFilterUpdates = useSbSelector(state => state.filters.hasNewFilterUpdates);
    const appliedFilters = useFragment(appliedFiltersFragment, appliedFiltersRef);
    const dispatch = useDispatch();
    const handleRefineMenuClose = useCallback<HandleRefineMenuClose>(
        ({ areFiltersApplied }) => {
            if (areFiltersApplied) {
                // Format: `categoryL1:furniture,color:blue,color:red,userType:consumer`
                const label = appliedFilters
                    ?.reduce<string[]>((result, appliedFilter) => {
                        const { name, values } = appliedFilter || {};

                        values?.forEach(value => {
                            result.push(`${name}:${value?.urlLabel}`);
                        });
                        return result;
                    }, [])
                    .join(',');

                const action = hasNewFilterUpdates ? 'view results' : 'close filter menu';
                trackEvent({
                    category: 'results page refinement',
                    eventName: eventNameConstants.EVENT_RESULTS_PAGE_REFINEMENT,
                    action,
                    interaction_type: action,
                    label,
                    step_interaction_name: label,
                });
            } else {
                // even though this action will still result in filtered results being displayed if any filters were applied
                // the event that fires should just capture that the user clicked the X
                trackEvent({
                    eventName: eventNameConstants.EVENT_RESULTS_PAGE_REFINEMENT,
                    interaction_type: 'exit modal',
                    step_interaction_name: 'filter modal',
                });
            }
            dispatch(toggleFilterDisplay(false));
        },
        [appliedFilters, dispatch, hasNewFilterUpdates]
    );

    return handleRefineMenuClose;
};
